import { Check, X } from "lucide-react";
import { OrderStage, Order } from "./types";
import { formatIndianCurrency, formatNumberWithCommas } from "@/app/utils/helper";
import { useEffect } from "react";

interface TimelineProps {
    orderDetails: Order;
    setmobileOrderExpanded: (value: boolean) => void;
    mobileOrderExpanded: boolean;
}

export function OrderTimeline({
    orderDetails,
    mobileOrderExpanded,
    setmobileOrderExpanded,
}: TimelineProps) {

    useEffect(() => {
        if (mobileOrderExpanded) {
            document.body.style.overflow = "hidden";
        } else {
            document.body.style.overflow = "";
        }

        return () => {
            document.body.style.overflow = "";
        };
    }, [mobileOrderExpanded]);

    const getStageColors = (stage: OrderStage) => {
        if (stage.value === "Order Rejected" || stage.value === "Order Cancelled" || stage.value === "Payment Failed") {
            return "bg-error-500";
        } else if ((stage.value === "Payment Received" && !stage.date) || stage.status === "inProgress") {
            return "bg-amber-500";
        } else if (stage.isCompleted) {
            return "bg-success-700";
        } else {
            return "bg-success-700";
        }
    };

    const getTextColors = (stage: OrderStage) => {
        if (stage.value === "Order Failed" || stage.value === "Order Cancelled" || stage.value === "Order Rejected" || stage.value === "Payment Failed") {
            return "text-red-500";
        }
        if (stage.value === "Order Created") return "text-success-700";
        if (stage.value === "Order Processed") {
            return stage.isCompleted && stage.date
                ? "text-success-700"
                : stage.status === "inProgress"
                  ? "text-amber-500"
                  : "text-neutral-300";
        }
        if (stage.value === "Payment Received") {
            return stage.isCompleted && stage.date ? "text-success-700" : !stage.isCompleted ? "text-neutral-300" : "text-amber-500";
        }
        if (stage.value === "Order Settled" && stage.isCompleted) {
            return "text-success-700";
        } else {
            return "text-neutral-300";
        }
    };

    return (
        <div
            className={`fixed inset-0 flex items-end justify-center bg-black bg-opacity-0 transition-opacity duration-300 ${mobileOrderExpanded ? "opacity-100" : "opacity-0"}`}
        >
            <div
                className={`overflow-auto w-full max-w-md transform overflow-y-auto rounded-t-lg bg-white p-6 transition-transform duration-500 ${mobileOrderExpanded ? "translate-y-0" : "translate-y-full"}`}
            >
                <div className="mb-2 flex justify-end">
                    <button
                        className="text-gray-500 hover:text-gray-700"
                        onClick={() => setmobileOrderExpanded(false)}
                    >
                        <X size={24} onClick={() => setmobileOrderExpanded(false)} />
                    </button>
                </div>

                <div className="mb-6 space-y-2 text-sm">
                    <div className="flex gap-1 text-sm tracking-[0.14px] leading-[160%] font-medium">
                        <span className="text-neutral-300">Type:</span>
                        <span>{orderDetails?.details?.type}</span>
                    </div>
                    <div className="flex gap-1 text-sm tracking-[0.14px] leading-[160%] font-medium">
                        <span className="text-neutral-300">Price per share:</span>
                        <span>
                            {formatIndianCurrency(orderDetails?.details?.pricePerShare ?? 0)}
                        </span>
                    </div>
                    <div className="flex gap-1 text-sm tracking-[0.14px] leading-[160%] font-medium">
                        <span className="text-neutral-300">Units:</span>
                        <span>
                            {formatNumberWithCommas(orderDetails?.details?.units ?? 0)}
                        </span>
                    </div>
                    <div className="flex gap-1 text-sm tracking-[0.14px] leading-[160%] font-medium">
                        <span className="text-neutral-300 text-nowrap">Transaction Id:</span>
                        <span>{orderDetails?.details?.transactionId}</span>
                    </div>
                </div>

                <div className="relative max-h-[50vh] overflow-y-auto">
                    {orderDetails.details?.stages?.map((item, index) => (
                        <div key={index} className="relative flex items-start">
                            <div className="mr-4 flex flex-col items-center">
                                <div
                                    className={`flex h-[25px] w-[25px] flex-col items-center justify-center rounded-full ${
                                        item.isCompleted &&
                                        item.value !== "Order Failed" &&
                                        item.value !== "Order Rejected" && item.value !== "Order Cancelled"
                                            ? getStageColors(item)
                                            : item.value === "Order Rejected" ||
                                                item.value === "Order Failed" || 
                                                item.value === "Order Cancelled" ||
                                                item.value === "Payment Failed"
                                              ? "bg-error-500"
                                              : "bg-gray-200"
                                    }`}
                                >
                                    {item.isCompleted &&
                                    item.value !== "Order Failed" &&
                                    item.value !== "Order Rejected" && item.date ? (
                                        <Check className="text-white" size={15} />
                                    ) : null}
                                    {item.value === "Order Failed" ||
                                    item.value === "Order Rejected" || item.value === "Order Cancelled" || item.value === "Payment Failed" ? (
                                        <X className="text-white" size={16} />
                                    ) : (
                                        <></>
                                    )}
                                </div>
                                {orderDetails.details?.stages &&
                                    index < orderDetails.details?.stages?.length - 1 && (
                                        <div
                                            className={`h-16 w-[4px] ${
                                                item.isCompleted
                                                    ? item.value === "Order Failed" ||
                                                      item.value === "Order Rejected" ||
                                                      item.value === "Payment Failed" || 
                                                      orderDetails.details?.stages[0].value ===
                                                          "Order Failed"
                                                        ? "bg-error-500"
                                                        : getStageColors(item)
                                                    : "bg-gray-200"
                                            }`}
                                        />
                                    )}
                            </div>
                            <div>
                                <p
                                    className={`text-xs font-medium ${item.value === "Order Rejected" || item.value === "Order Failed" ? "text-error-500" : getTextColors(item)}`}
                                >
                                    {item.value}
                                </p>
                                {item?.estimatedSettlementDate && (
                                    <p className="text-[10px] text-gray-500">
                                        {item?.estimatedSettlementDate}
                                        {item?.date}
                                    </p>
                                )}
                                <div className="text-[10px] text-gray-500">{`${item.label === "Order Settled" ? 'Estimated: ' : ''} ${item.date || "--"}`}</div>
                                {item.isCompleted && item.label === "Order Settled" ? <p className="text-xs text-gray-500">{item.actualSettlementDate || "--"}</p> : null}
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}
